/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react'

import React from 'react'
import { View } from '@components/atoms'
import { Footer, Seo } from '@components/systems'
import { mono, monoShared } from '@components/systems/monolith/monolith.tokens'

import { useNavigate } from 'react-router-dom'

/**
 * 존재하지 않는 경로 — Monolith(2안). 구 페이지는 `pages/home/NotFound.page.tsx` 에 보관.
 *
 * 예전에는 `path: '*'` 가 홈으로 리다이렉트했다. CloudFront 가 없는 경로까지
 * 200 + index.html 로 응답하기 때문에(PENDING_CHANGES 8번), 리다이렉트만으로는
 * 크롤러 입장에서 "홈과 같은 내용의 페이지가 무한히 있는" 상태가 된다.
 * 여기서 noindex 를 명시해 색인을 막는다 — 그 판단은 그대로 두고 외형만 바꿨다
 * (구 페이지의 radius 999 알약 버튼은 DESIGN_SYSTEM §5 금지 항목이었다).
 */
const MonolithNotFoundPage = () => {
	// hook
	const navigate = useNavigate()

	const linkList = [
		{ label: 'Shop', path: '/shop' },
		{ label: 'Products', path: '/products' },
		{ label: 'Support', path: '/support' },
		{ label: 'Contact', path: '/contact' },
	]

	return (
		<View css={style}>
			<Seo
				title="Page Not Found | Judahs"
				description="The page you were looking for does not exist."
				noindex={true}
			/>

			<View className="mnWrap">
				<View className="mn404">
					<span className="mnKicker">404</span>
					<h1 className="mn404Title">This page does not exist.</h1>
					<p className="mn404Body">
						The link may be out of date, or the address may have a typo.
					</p>

					<button type="button" className="mnCta mn404Cta" onClick={() => navigate('/')}>
						Go to home
					</button>

					<View className="mn404Links">
						{linkList.map(aLink => (
							<button
								key={aLink.path}
								type="button"
								className="mn404Link"
								onClick={() => navigate(aLink.path)}
							>
								{aLink.label}
							</button>
						))}
					</View>
				</View>
			</View>

			<Footer />
		</View>
	)
}

export default MonolithNotFoundPage

const style = css({
	...monoShared,
	position: 'relative',
	width: '100%',

	'.mn404': {
		display: 'flex',
		flexDirection: 'column',
		alignItems: 'center',
		textAlign: 'center',
		padding: 'clamp(90px, 12vw, 170px) 0 clamp(90px, 12vw, 170px)',

		'.mn404Title': {
			marginTop: 28,
			fontSize: 'clamp(32px, 4.4vw, 60px)',
			lineHeight: 1.1,
			fontWeight: 200,
			letterSpacing: '-0.01em',
			color: mono.ink,
		},

		'.mn404Body': {
			marginTop: 22,
			fontSize: 17,
			lineHeight: 1.7,
			fontWeight: 300,
			color: mono.dim,
			maxWidth: 460,
		},

		'.mn404Cta': {
			marginTop: 48,
		},

		'.mn404Links': {
			display: 'flex',
			flexWrap: 'wrap',
			justifyContent: 'center',
			gap: 32,
			marginTop: 56,

			'.mn404Link': {
				padding: 0,
				border: 0,
				background: 'transparent',
				fontSize: 13,
				fontWeight: 500,
				color: mono.faint,
				cursor: 'pointer',
				borderBottom: '1px solid transparent',
				paddingBottom: 2,
				transition: 'color 200ms ease-out',

				'&:hover': {
					color: mono.ink,
					borderBottomColor: mono.ink,
				},
			},
		},
	},

	'@media (prefers-reduced-motion: reduce)': {
		'.mn404Link': { transition: 'none' },
	},
})
